import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";

export interface SubscriptionStatus {
  subscription?: {
    id: string;
    status: string;
    currentPeriodStart: Date | string;
    currentPeriodEnd: Date | string;
    cancelAtPeriodEnd?: boolean;
  };
  isSubscribed: boolean;
  isOnTrial: boolean;
  needsSubscription: boolean;
}

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  refreshSubscriptionData: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Endpoint removed - using default values
      // TODO: Replace with new subscription endpoint when available
      setSubscriptionStatus({
        isSubscribed: false,
        isOnTrial: false,
        needsSubscription: false,
      });
    } catch (err: any) {
      console.error("Failed to fetch subscription status:", err);
      setError(err.message || "Failed to load subscription status");
      // Set default values if fetch fails
      setSubscriptionStatus({
        isSubscribed: false,
        isOnTrial: false,
        needsSubscription: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionStatus,
        loading,
        error,
        refreshSubscriptionData: fetchSubscriptionStatus,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

